import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import consumer from "../channels/consumer"

// Tracks the user's attendance on an attendable (identified by a signed GID).
// The browser heartbeats over an ActionCable subscription (a closed tab stops
// beating and ages out); it also stamps attendance over HTTP on mount and on
// tab/network return as a backstop. The page sets turbo-cache-control: no-preview
// so a revisit doesn't double-mount.
const REFRESH_MS = 15_000

export default class extends Controller {
  static values = { sgid: String, schoolClassId: Number, url: String }

  connect() {
    // sid makes each subscription identifier unique per mount. Without it, re-subscribing
    // to the same attendable can collide with a prior unsubscribe still in flight, so the
    // server drops the new subscribe with no confirmation and attendance never re-registers
    // — a proxy's latency makes it reliable.
    this.subscription = consumer.subscriptions.create({
      channel: "AttendanceChannel",
      sgid: this.sgidValue,
      school_class_id: this.schoolClassIdValue,
      sid: crypto.randomUUID()
    })
    this.timer = setInterval(() => this.subscription.perform("refresh"), REFRESH_MS)

    this.recover = this.recover.bind(this)
    window.addEventListener("online", this.recover)
    document.addEventListener("visibilitychange", this.recover)
    this.stamp() // reliable initial attendance even if the socket subscribe is orphaned
  }

  disconnect() {
    clearInterval(this.timer)
    window.removeEventListener("online", this.recover)
    document.removeEventListener("visibilitychange", this.recover)
    this.subscription?.unsubscribe()
  }

  // On network/tab return, make the socket re-evaluate (reconnect + re-subscribe) and
  // re-stamp over HTTP, so attendance recovers even if the socket had quietly gone bad.
  recover() {
    if (document.hidden) return
    consumer.connection.monitor.visibilityDidChange()
    this.stamp()
  }

  stamp() {
    post(this.urlValue, { body: { sgid: this.sgidValue, school_class_id: this.schoolClassIdValue } })
  }
}
